// Add functions here which may be shared across future graph types
import { select, selectAll, style } from 'd3-selection';
import { format, formatLocale, formatDefaultLocale } from 'd3-format';
import { csv } from 'd3-fetch';
import { utcFormat, utcParse, timeFormat } from 'd3-time-format';
import { timeYear } from 'd3-time';
import { line } from 'd3-shape';
import { scaleTime, scaleLinear } from 'd3-scale';
import { min, max, extent } from 'd3-array';
import { ticks, axisLeft, axisBottom } from 'd3-axis';
import { useState, useRef, useEffect } from 'react';

// assign d3 modules to an object so we can use standard d3 syntax like d3.select
export const d3 = {
  select,
  selectAll,
  formatLocale,
  csv,
  scaleTime,
  scaleLinear,
  axisLeft,
  axisBottom,
  min,
  max,
  extent,
  style,
  format,
  utcFormat,
  timeFormat,
  line,
  formatDefaultLocale,
  utcParse,
  timeYear,
  ticks,
};

const dateParsers = [d3.utcParse('%Y'), d3.utcParse('%Y-%m-%d')];

export const formatYear = (string) => {
  let parsed = null;
  dateParsers.forEach((parser) => {
    if (!parsed) {
      parsed = parser(string);
    }
  });

  return parsed;
};

export const parseNumbers = (dataSets) => {
  dataSets.forEach((d, i) =>
    dataSets.columns.forEach((col) => {
      if (col !== 'Year') {
        dataSets[i][col] = parseFloat(
          dataSets[i][col].toString().replace(/,/g, ''),
          10
        );
      }
    })
  );
  return dataSets;
};

export const formatDatasets = (dataSets) =>
  dataSets.columns.slice(1).map((set) => ({
    set,
    values: dataSets.map((d) => ({
      key: formatYear(d.Year),
      value: +d[set],
    })),
  }));

const locale = d3.formatLocale({
  decimal: '.',
  thousands: ',',
  grouping: [3],
  currency: ['£', ''],
});

export const numberFormats = {
  percent: (d) => `${d}%`,
  pound: (d) => `${locale.format('($,')(d)}`,
  default: (d) => `${locale.format(',')(d)}`,
};

export const xAxisTickValues = (startValue, stopValue, noOfTicks) => {
  const arr = [];
  const step = (stopValue - startValue) / (noOfTicks - 1);
  for (let i = 0; i < noOfTicks; i += 1) {
    arr.push(formatYear(Math.ceil(startValue + step * i)));
  }
  return arr;
};

export const lineColorsMap = {
  red: '#eb001f',
  pink: '#f750a0',
  blue: '#269af2',
  black: '#000000',
  labour: '#d50000',
  conservative: '#0087dc',
  libdem: '#fdbb30',
  greenparty: '#008066',
};

export const GetElementWidth = (elRef) => {
  const [elementWidth, setElementWidth] = useState(null);

  // Check if we're in a browser or in node and don't call new ResizeObserver if in node
  const observer = useRef(
    typeof window !== 'undefined'
      ? new ResizeObserver((entries) => {
          const { width } = entries[0].contentRect;

          setElementWidth(width);
        })
      : null
  );

  useEffect(() => {
    if (!observer) return;

    const currentObserver = observer.current;
    const currentRef = elRef.current;

    if (currentRef) {
      currentObserver.observe(currentRef);
    }

    return () => {
      currentObserver.unobserve(currentRef);
    };
  }, [elRef, observer]);

  return Math.ceil(elementWidth);
};
